/**
 * Bons cuisine non imprimés (flag opt-in `impression_alerte_echec`).
 * Avant : un toast « Réessayer » de 10 s, puis plus aucune trace → commande perdue
 * pour la cuisine. Ici : liste persistante (mémoire + localStorage de l'appareil, 12 h,
 * 20 max) affichée par PrintFailureBanner, vidée dès que le bon sort.
 */
import type { KotPrintRetryParams } from './kotPrintRetry';

export interface KotFailure {
  readonly id: string;
  readonly label: string;
  readonly at: number;
  readonly params: KotPrintRetryParams;
}

const STORAGE_KEY = 'vp_kot_failures_v1';
const MAX_ITEMS = 20;
const TTL_MS = 12 * 3600_000;

const load = (): KotFailure[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return (parsed as KotFailure[]).filter((f) => Date.now() - f.at < TTL_MS);
  } catch {
    return [];
  }
};

let items: KotFailure[] = load();
const listeners = new Set<() => void>();

const commit = (next: KotFailure[]) => {
  items = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* stockage plein ou indisponible : la liste reste en mémoire */
  }
  listeners.forEach((l) => l());
};

/** Même envoi = même entrée (clé d'idempotence, sinon n° de commande + libellé). */
export const kotFailureId = (p: KotPrintRetryParams): string =>
  p.idempotencyKey ?? `${p.payload.orderNumber}:${p.label ?? 'cuisine'}`;

export const kotFailures = {
  list: (): KotFailure[] => items,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  push: (params: KotPrintRetryParams): void => {
    const id = kotFailureId(params);
    const entry: KotFailure = { id, label: params.label ?? 'cuisine', at: Date.now(), params };
    commit([entry, ...items.filter((f) => f.id !== id)].slice(0, MAX_ITEMS));
  },
  resolve: (id: string): void => {
    if (items.some((f) => f.id === id)) commit(items.filter((f) => f.id !== id));
  },
};
